/**
 * SA review moderation queue (Feb 2026, Slice 2).
 * Route: /dispatch/admin/reviews
 * Filters between pending / approved / rejected. Approve or reject with
 * optional reason. Slice 3 will award points on approval.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeET } from '../../lib/timeET';
import { Navbar } from '../../components/Navbar';
import { ReviewWindowSettings } from '../../components/reviews/ReviewWindowSettings';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

function StarRow({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-3 h-3 ${n <= value ? 'fill-[#FFD60A] text-[#FFD60A]' : 'text-[#2A0F3D]'}`} />
      ))}
    </div>
  );
}

export default function ReviewModerationPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/reviews?status=${filter}&limit=200`,
        { withCredentials: true },
      );
      setReviews(data.reviews || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reviews');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, decision) => {
    setBusyId(r.id);
    let reason = null;
    if (decision === 'rejected') {
      reason = window.prompt('Reason for rejection (shown to customer, optional):', '');
      if (reason === null) { setBusyId(null); return; } // cancelled
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/reviews/${r.id}/${decision === 'approved' ? 'approve' : 'reject'}`,
        decision === 'rejected' ? { reason } : {},
        { withCredentials: true },
      );
      toast.success(decision === 'approved' ? 'Review approved · points award on the way' : 'Review rejected');
      setReviews((prev) => prev.filter((x) => x.id !== r.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="review-moderation-page">
      <Navbar />
      <div className="p-4">
      {/* Feb 2026 — back button. Reformatted so page now uses global Navbar
          instead of ad-hoc safe-area padding. */}
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate('/dispatch')}
          className="p-1.5 border border-[#2A0F3D] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050003] transition-colors"
          data-testid="review-moderation-back"
          aria-label="Back to dispatch"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p className="text-[#39FF14] font-mono text-lg font-bold uppercase tracking-wider">Review Moderation</p>
        <span className="text-[#4B6B52] font-mono text-xs">// {reviews.length} in {filter}</span>
      </div>

      <ReviewWindowSettings />

      <div className="flex gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setFilter(s.key)}
            className={`px-3 py-1 font-mono text-xs uppercase border ${
              filter === s.key
                ? 'bg-[#39FF14] text-[#050003] border-[#39FF14]'
                : 'bg-transparent text-[#39FF14] border-[#2A0F3D] hover:border-[#39FF14]'
            }`}
            data-testid={`review-filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-[#4B6B52] font-mono text-xs">// loading…</p>}
      {!loading && reviews.length === 0 && (
        <p className="text-[#4B6B52] font-mono text-xs">// no reviews in this queue</p>
      )}

      <div className="space-y-3">
        {reviews.map((r) => (
          <div
            key={r.id}
            className="border border-[#2A0F3D] p-3 bg-[#0C0612]"
            data-testid={`review-mod-${r.id}`}
          >
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-[#39FF14] font-mono text-xs font-bold truncate">
                  {r.customer_name || r.customer_email || 'Unknown'}
                </p>
                <StarRow value={r.rating} />
                <span className="text-[#4B6B52] font-mono text-[10px]">on</span>
                <p className="text-[#A3C7A8] font-mono text-xs truncate">{r.product_name}</p>
              </div>
              <p className="text-[10px] font-mono text-[#4B6B52]">{formatDateTimeET(r.created_at)}</p>
            </div>

            <p className="text-[#A3C7A8] font-mono text-sm whitespace-pre-wrap break-words mb-2">{r.body}</p>

            {(r.media_ids || []).length > 0 && (
              <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
                {r.media_ids.map((id) => (
                  <img
                    key={id}
                    src={`${API_URL}/api/uploads/media/${id}`}
                    alt="review"
                    className="w-24 h-24 object-cover border border-[#2A0F3D] flex-shrink-0"
                    loading="lazy"
                  />
                ))}
              </div>
            )}

            {filter === 'pending' && (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => decide(r, 'approved')}
                  className="flex items-center gap-1 bg-[#39FF14] text-[#050003] font-mono text-xs font-bold uppercase px-3 py-1.5 hover:bg-[#5FFF3F] disabled:opacity-40"
                  data-testid={`review-approve-${r.id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => decide(r, 'rejected')}
                  className="flex items-center gap-1 border border-[#5A0F1F] text-[#FF3B30] font-mono text-xs uppercase px-3 py-1.5 hover:bg-[rgba(255,59,48,0.1)] disabled:opacity-40"
                  data-testid={`review-reject-${r.id}`}
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            )}
            {r.rejection_reason && (
              <p className="text-[#FF3B30] font-mono text-[11px] mt-2">Rejected: {r.rejection_reason}</p>
            )}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
